// Разбор табличных данных из буфера: HTML (Excel, Word, Google Таблицы, LibreOffice, браузер) и текст
// (TSV из Excel, CSV, Markdown-таблица) -> матрица ячеек с объединениями и головкой.
import { DOMParser as PMDOMParser, type Schema } from '@tiptap/pm/model';
import type { MCell, Matrix } from './model';
import { parseDelimited, trimMatrix } from './model';

/** HTML буфера - «только таблица» (Excel, Google Таблицы, выделенная таблица Word)? */
export function isTableOnlyHtml(html: string): boolean {
  if (!/<table[\s>]/i.test(html)) return false;
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const tables = doc.body.querySelectorAll('table');
  if (!tables.length) return false;
  const top = Array.from(tables).filter((t) => !t.parentElement?.closest('table'));
  if (top.length !== 1) return false;
  const clone = doc.body.cloneNode(true) as HTMLElement;
  clone.querySelectorAll('table, style, script, meta, link, title').forEach((e) => e.remove());
  return !(clone.textContent || '').replace(/[\s ]+/g, '') && !clone.querySelector('img');
}

/** Есть ли в HTML таблица. */
export function hasTable(html: string): boolean {
  return /<table[\s>]/i.test(html);
}

/** Первая таблица верхнего уровня из HTML -> матрица. Содержимое ячеек разбирается схемой редактора
 *  (жирный, курсив, индексы, ссылки сохраняются; стили Excel/Word отбрасываются). */
export function parseHtmlTable(html: string, schema: Schema): Matrix | null {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const table = Array.from(doc.body.querySelectorAll('table')).find((t) => !t.parentElement?.closest('table'));
  if (!table) return null;
  const parser = PMDOMParser.fromSchema(schema);
  const trs = Array.from(table.querySelectorAll('tr')).filter((tr) => tr.closest('table') === table);
  if (!trs.length) return null;
  let headerRows = 0;
  let headerDone = false;
  const rows: MCell[][] = [];
  for (const tr of trs) {
    const tds = Array.from(tr.children).filter((c) => c.tagName === 'TD' || c.tagName === 'TH') as HTMLElement[];
    if (!tds.length) continue;
    const inThead = !!tr.closest('thead');
    const allTh = tds.every((c) => c.tagName === 'TH');
    if (!headerDone && (inThead || allTh)) headerRows++;
    else headerDone = true;
    rows.push(tds.map((td) => cellFrom(td, parser, schema)));
  }
  if (!rows.length) return null;
  // Excel/Google: головка не размечена - первая строка; объединение по вертикали в первой строке -> головка выше
  if (!headerRows) {
    headerRows = 1;
    for (const c of rows[0]) headerRows = Math.max(headerRows, Math.min(3, c.rowspan));
  }
  return trimMatrix({ rows, headerRows });
}

function cellFrom(td: HTMLElement, parser: PMDOMParser, schema: Schema): MCell {
  const colspan = Math.max(1, Math.min(50, parseInt(td.getAttribute('colspan') || '1', 10) || 1));
  const rowspan = Math.max(1, Math.min(500, parseInt(td.getAttribute('rowspan') || '1', 10) || 1));
  // чистка: переводы строк Excel (<br style=mso-data-placement:same-cell>), неразрывные пробелы, пустые <p>
  const box = td.cloneNode(true) as HTMLElement;
  box.querySelectorAll('style, script, img, table').forEach((e) => e.remove());
  const cellNode = parser.parse(box, { topNode: schema.nodes.tableCell.create(), preserveWhitespace: false });
  const json = cellNode.toJSON();
  let content = (json.content || []).filter((p: any) => p.type === 'paragraph');
  // пустые абзацы в середине не нужны
  content = content.filter((p: any, i: number) => (p.content && p.content.length) || i === 0);
  for (const p of content) {
    for (const t of p.content || []) if (t.type === 'text') t.text = t.text.replace(/ /g, ' ');
    trimParagraph(p);
  }
  // выравнивание источника не переносим: по СТО его задаёт редактор (боковик влево, значения по центру)
  return { content, colspan, rowspan, align: null };
}

/** Убрать пробелы по краям абзаца. */
function trimParagraph(p: any) {
  const c = p.content || [];
  while (c.length && c[0].type === 'text' && !c[0].text.trim()) c.shift();
  while (c.length && c[c.length - 1].type === 'text' && !c[c.length - 1].text.trim()) c.pop();
  if (c.length && c[0].type === 'text') c[0].text = c[0].text.replace(/^\s+/, '');
  if (c.length && c[c.length - 1].type === 'text') c[c.length - 1].text = c[c.length - 1].text.replace(/\s+$/, '');
  if (c.length) p.content = c; else delete p.content;
}

/** Текст буфера похож на таблицу (TSV из Excel/Sheets: табуляции, одинаковое число граф)? */
export function looksLikeTsv(text: string): boolean {
  const lines = text.replace(/\r\n?/g, '\n').replace(/\n+$/, '').split('\n');
  if (lines.length < 1) return false;
  const counts = lines.map((l) => (l.match(/\t/g) || []).length);
  return counts[0] > 0 && counts.every((c) => c === counts[0]) && (lines.length > 1 || counts[0] >= 1);
}

/** Markdown-таблица «| a | b |» + «|---|---|». */
export function looksLikeMarkdownTable(text: string): boolean {
  const lines = text.trim().split(/\r?\n/);
  return lines.length >= 2 && /^\s*\|.*\|\s*$/.test(lines[0]) && /^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)*\|?\s*$/.test(lines[1]);
}

/** Текст -> матрица (TSV, CSV, «;», Markdown). */
export function parseTextTable(text: string, delim?: string): Matrix | null {
  if (!delim && looksLikeMarkdownTable(text)) return parseDelimited(text, '|');
  return parseDelimited(text, delim);
}
